import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BulkOrders } from "../lib/api.js";
import { Button, Banner, COLORS as C } from "../components/UI.jsx";

export default function BulkUpload() {
  const navigate = useNavigate();
  if (!auth.loggedIn()) { navigate("/login", { replace: true }); return null; }

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const blob = await BulkOrders.template();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shiftswift_bulk_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFile = (f) => {
    setError(null);
    setResult(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("File must be a .csv");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("File exceeds 5MB. Split into smaller batches.");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await BulkOrders.uploadCsv(file);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.cream, fontFamily: "-apple-system, system-ui, Inter, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.line}`, padding: "16px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: C.red, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontWeight: 800, fontSize: 16 }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>Shiftswift</div>
          </Link>
          <Link to="/dashboard" style={{ fontSize: 13, color: C.text2, textDecoration: "none", fontWeight: 600 }}>← Back to dashboard</Link>
        </div>
      </div>

      {/* Header */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 16px" }}>
        <div style={{ fontSize: 11, color: C.red, letterSpacing: 1.5, fontWeight: 700, marginBottom: 8 }}>BULK ORDERS</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.8 }}>
          Upload up to 200 orders
        </h1>
        <p style={{ fontSize: 15, color: C.text2, marginTop: 8, lineHeight: 1.6 }}>
          Download the template, fill in your orders, drop the file here. Each row creates a shipment.
          Bad rows are flagged so you can fix and re-upload.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 60px" }}>
        {/* Step 1 */}
        <Section number={1} title="Download the template">
          <p style={{ fontSize: 14, color: C.text2, lineHeight: 1.6, marginTop: 0 }}>
            CSV with all required columns and 3 sample rows. Open it in Excel, Numbers, or Google Sheets.
          </p>
          <Button onClick={downloadTemplate} fullWidth={false} variant="secondary" style={{ padding: "10px 18px", fontSize: 13 }}>
            ⬇ Download template
          </Button>
        </Section>

        {/* Step 2 */}
        <Section number={2} title="Upload your filled CSV">
          {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

          {!result && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? C.red : C.border}`,
                borderRadius: 10,
                background: dragOver ? `${C.red}08` : C.white,
                padding: "44px 24px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 10 }}>📄</div>
              {file ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>
                    {(file.size / 1024).toFixed(1)} KB · click to change
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
                    Drop your CSV here, or click to browse
                  </div>
                  <div style={{ fontSize: 12, color: C.text3, marginTop: 6 }}>
                    Up to 5MB · CSV only
                  </div>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </div>
          )}

          {file && !result && (
            <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
              <Button onClick={() => setFile(null)} variant="secondary" fullWidth={false}>
                Choose different file
              </Button>
              <Button onClick={upload} busy={busy} fullWidth={false} style={{ flex: 1 }}>
                {busy ? "Processing…" : `Upload and create orders`}
              </Button>
            </div>
          )}
        </Section>

        {/* Results */}
        {result && (
          <Section number={3} title="Results">
            <ResultsPanel result={result} onReset={() => { setFile(null); setResult(null); }} />
          </Section>
        )}

        {/* Info */}
        {!result && (
          <div style={{
            marginTop: 24, padding: 20, background: C.white,
            border: `1px solid ${C.line}`, borderRadius: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 10 }}>
              What happens next?
            </div>
            <ol style={{ fontSize: 13, color: C.text2, lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
              <li>We validate every row before creating any orders</li>
              <li>Successful orders show up in your dashboard immediately</li>
              <li>Errors are listed row-by-row so you can fix and re-upload</li>
              <li>You can book all created orders together with one click</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 14,
          background: C.navy, color: C.white,
          fontSize: 13, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {number}
        </div>
        <h2 style={{ fontSize: 17, fontWeight: 800, color: C.navy, margin: 0 }}>{title}</h2>
      </div>
      <div style={{
        background: C.white, border: `1px solid ${C.line}`,
        borderRadius: 10, padding: 24,
      }}>
        {children}
      </div>
    </div>
  );
}

function ResultsPanel({ result, onReset }) {
  const navigate = useNavigate();
  const errors = result.rows.filter((r) => r.status === "error");
  const successes = result.rows.filter((r) => r.status === "ok");

  return (
    <div>
      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        <Stat label="Total rows" value={result.total_rows} color={C.navy} />
        <Stat label="Created" value={result.successful} color={C.green} />
        <Stat label="Failed" value={result.failed} color={result.failed > 0 ? "#C8302A" : C.text3} />
      </div>

      {result.successful > 0 && (
        <Banner kind="success">
          ✓ {result.successful} order{result.successful !== 1 ? "s" : ""} created successfully.
          They're now in your <Link to="/dashboard" style={{ color: C.green, fontWeight: 700 }}>dashboard</Link>.
        </Banner>
      )}

      {/* Errors first */}
      {errors.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.errorText, marginBottom: 10 }}>
            {errors.length} row{errors.length !== 1 ? "s" : ""} need attention
          </div>
          <div style={{ border: `1px solid ${C.errorBorder}`, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ background: C.errorBg, padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.errorText, letterSpacing: 0.5 }}>
              ROW · ERROR
            </div>
            {errors.slice(0, 50).map((r, i) => (
              <div key={i} style={{
                padding: "10px 14px",
                borderTop: i > 0 ? `1px solid ${C.errorBorder}` : "none",
                fontSize: 13, display: "flex", gap: 12, background: C.white,
              }}>
                <div style={{ fontWeight: 700, color: C.text2, minWidth: 50 }}>Row {r.row}</div>
                <div style={{ flex: 1 }}>
                  {r.receiver_name && <span style={{ color: C.text3, marginRight: 8 }}>{r.receiver_name} →</span>}
                  <span style={{ color: C.errorText }}>{r.error}</span>
                </div>
              </div>
            ))}
            {errors.length > 50 && (
              <div style={{ padding: 12, fontSize: 12, color: C.text3, textAlign: "center" }}>
                …and {errors.length - 50} more errors
              </div>
            )}
          </div>
          <p style={{ fontSize: 12, color: C.text3, marginTop: 10, lineHeight: 1.6 }}>
            Fix these rows in your CSV and upload again.
            Only fix the failed rows — successful orders are already created.
          </p>
        </div>
      )}

      {/* Success preview */}
      {successes.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 10 }}>
            Created orders
          </div>
          <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, overflow: "hidden" }}>
            {successes.slice(0, 10).map((r, i) => (
              <div key={i} style={{
                padding: "10px 14px",
                borderTop: i > 0 ? `1px solid ${C.line}` : "none",
                fontSize: 13, display: "flex", gap: 12, background: C.white,
              }}>
                <div style={{ fontFamily: "monospace", fontWeight: 700, color: C.navy, minWidth: 100 }}>
                  {r.order_number}
                </div>
                <div style={{ flex: 1, color: C.text2 }}>
                  {r.receiver_name} → {r.receiver_pincode}
                </div>
                <div style={{ color: C.green, fontSize: 12, fontWeight: 600 }}>✓ Created</div>
              </div>
            ))}
            {successes.length > 10 && (
              <div style={{ padding: 12, fontSize: 12, color: C.text3, textAlign: "center", borderTop: `1px solid ${C.line}` }}>
                …and {successes.length - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <Button onClick={onReset} variant="secondary" fullWidth={false}>
          Upload another batch
        </Button>
        <Button onClick={() => navigate("/dashboard")} fullWidth={false} style={{ flex: 1 }}>
          Go to dashboard
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: C.cream, borderRadius: 8, padding: 16, textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: -1 }}>{value}</div>
      <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginTop: 4 }}>{label}</div>
    </div>
  );
}
